/**
 * RequestInvitePage
 *
 * Story 15: Registration Page Routes
 *
 * Page for requesting invite codes
 *
 * Features:
 * - Render RequestInviteForm component
 * - Display success message on submission
 * - Link to registration page
 * - Public route (no authentication required)
 * - Mobile-responsive card layout
 */

/* eslint-disable react/no-unescaped-entities */

import { Link } from 'react-router-dom';
import { RequestInviteForm } from '../components/RequestInviteForm';

export function RequestInvitePage() {
  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-neutral-900 mb-2">
              Request an Invite Code
            </h1>
            <p className="text-neutral-600">
              Don't have an invite code? Request one and we'll review your application soon.
            </p>
          </div>

          {/* Link to Registration - Visible above the fold */}
          <div className="mb-6 text-center">
            <p className="text-sm text-neutral-600">
              Already have an invite code?{' '}
              <Link
                to="/provider/register"
                className="font-medium text-primary-600 hover:text-primary-700"
              >
                Complete your registration
              </Link>
            </p>
          </div>

          {/* Card Container */}
          <div className="card">
            <div className="border-b border-neutral-200 pb-4 mb-6">
              <h2 className="text-xl font-semibold text-neutral-900">Application Information</h2>
              <p className="text-sm text-neutral-600 mt-1">
                Tell us about your waste management business
              </p>
            </div>

            <RequestInviteForm />
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-neutral-100 rounded-lg">
            <h3 className="text-sm font-semibold text-neutral-900 mb-2">What happens next?</h3>
            <ul className="space-y-2 text-sm text-neutral-700">
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>We'll review your application and respond soon</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>You'll receive an invite code via email</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Use the code to complete your registration</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
